import React from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import * as api from "src/services";
import { fCurrency } from "src/utils/formatNumber";
import { getImageUrl } from "src/utils/imageUrl";

const ThumbImgStyle = styled("img")(({ theme }) => ({
  width: 54,
  height: 54,
  minWidth: 54,
  objectFit: "cover",
  background: theme.palette.background.default,
  border: "1px solid " + theme.palette.divider,
  borderRadius: theme.shape.borderRadiusSm,
}));

function ProductThumb({ item, isLoading }) {
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    setHasError(false);
  }, [item?.image]);

  if (isLoading) {
    return <Skeleton variant="rounded" width={54} height={54} />;
  }

  if (item?.image && !hasError) {
    return (
      <ThumbImgStyle
        alt={item?.name || "Property"}
        src={getImageUrl(item.image)}
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <Avatar sx={{ width: 54, height: 54 }}>
      {item?.name?.slice(0, 1) || "P"}
    </Avatar>
  );
}

export default function ProductCard({
  item,
  isLoading,
  mutate,
  handleClickOpen,
  serialNumber,
  formType,
}) {
  const navigate = useNavigate();
  const { data: locationData } = useQuery(["locationData"], () =>
    api.getTrueLocation("")
  );
  const matchedLocation = locationData?.data?.filter((location) =>
    item?.location?.includes(location._id)
  );
  const locations =
    matchedLocation?.map((location) => location.name).join(", ") ||
    (typeof item?.location === "string" ? item.location : "");
  const isActive =
    typeof item?.is_active === "boolean"
      ? item.is_active
      : item?.status?.toLowerCase?.() === "active";
  const canEditProperty = Boolean(formType && item?.slug);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <ProductThumb item={item} isLoading={isLoading} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                #{serialNumber}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {isLoading ? <Skeleton width="80%" /> : item?.name || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isLoading ? <Skeleton width="45%" /> : fCurrency(item?.price || 0)}
              </Typography>
            </Box>

            {isLoading ? (
              <Skeleton variant="circular" width={34} height={34} />
            ) : canEditProperty ? (
              <IconButton
                size="small"
                onClick={() => navigate(`/products/${formType}/${item?.slug}`)}
                aria-label={`Edit ${item?.name || "property"}`}
              >
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            ) : handleClickOpen ? (
              <IconButton
                size="small"
                onClick={handleClickOpen(item?._id)}
                aria-label={`Open ${item?.name || "item"} actions`}
              >
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>

          <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
            {isLoading ? (
              <>
                <Skeleton variant="rounded" width={86} height={28} />
                <Skeleton variant="rounded" width={110} height={28} />
              </>
            ) : (
              <>
                <Chip
                  size="small"
                  label={isActive ? "Active" : "Inactive"}
                  color={isActive ? "success" : "default"}
                  variant={isActive ? "filled" : "outlined"}
                />
                {item?.coming_soon && (
                  <Chip size="small" label="Coming soon" variant="outlined" />
                )}
                {item?.is_featured && (
                  <Chip size="small" label="Featured" variant="outlined" />
                )}
              </>
            )}
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary">
              City
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.25 }}>
              {isLoading ? <Skeleton width="70%" /> : locations || "-"}
            </Typography>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ pt: 0.5 }}
          >
            <Typography variant="body2" color="text.secondary">
              Publish status
            </Typography>
            {isLoading ? (
              <Skeleton variant="rounded" width={54} height={32} />
            ) : mutate ? (
              <Switch
                checked={Boolean(isActive)}
                onChange={() => mutate({ id: item._id, formType })}
                inputProps={{ "aria-label": `Toggle ${item?.name || "property"}` }}
              />
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
